import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { apiClient } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { 
  ArrowLeft,
  Users, 
  TrendingUp, 
  TrendingDown,
  Target,
  Calendar,
  Percent,
  DollarSign,
  Lightbulb
} from "lucide-react";
import { toast } from "sonner";
import { GiftAidMetricsStrip } from "../components/GiftAidMetricsStrip";

export default function FundraiserDetail() {
  const { fundraiserId } = useParams();
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState(null);
  const [fundraiser, setFundraiser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [fundraiserId]);

  const loadData = async () => {
    try {
      const [metricsRes, usersRes] = await Promise.all([
        apiClient.get(`/metrics/fundraiser/${fundraiserId}`),
        apiClient.get("/users")
      ]);
      setMetrics(metricsRes.data);
      const user = usersRes.data.find(u => u.id === fundraiserId);
      setFundraiser(user);
    } catch (error) {
      toast.error("Failed to load fundraiser data");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-pulse text-slate-600">Loading...</div>
      </div>
    );
  }

  const stats = [
    { 
      label: "Total Signups", 
      value: metrics?.totalConversions || 0,
      icon: Users,
      color: "text-emerald-600"
    },
    { 
      label: "Conversion Rate", 
      value: `${((metrics?.conversionRate || 0) * 100).toFixed(0)}%`,
      icon: TrendingUp,
      color: "text-blue-600"
    },
    { 
      label: "No-Show Rate", 
      value: `${((metrics?.noShowRate || 0) * 100).toFixed(0)}%`,
      icon: TrendingDown,
      color: "text-amber-600"
    },
    { 
      label: "Avg Regular", 
      value: `£${metrics?.avgRegularDonation?.toFixed(0) || 0}`,
      icon: DollarSign,
      color: "text-violet-600"
    },
    { 
      label: "Avg One-Off", 
      value: `£${metrics?.avgOneOffDonation?.toFixed(0) || 0}`,
      icon: DollarSign,
      color: "text-pink-600"
    },
    { 
      label: "Double Ratio", 
      value: metrics?.singleDoubleRatio?.toFixed(1) || "0",
      icon: Target,
      color: "text-indigo-600"
    },
    { 
      label: "Avg Age", 
      value: metrics?.avgAge?.toFixed(0) || "0",
      icon: Calendar,
      color: "text-cyan-600"
    },
    { 
      label: "Engagement", 
      value: metrics?.avgEngagementScore?.toFixed(1) || "0",
      icon: Target,
      color: "text-orange-600"
    }
  ];

  const retentionStats = [
    { label: "30-Day Cancel", value: `${((metrics?.cancellation30d || 0) * 100).toFixed(0)}%` },
    { label: "90-Day Cancel", value: `${((metrics?.cancellation90d || 0) * 100).toFixed(0)}%` },
    { label: "All-Time Cancel", value: `${((metrics?.cancellationAllTime || 0) * 100).toFixed(0)}%` }
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="fundraiser-detail">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/owner")}
            className="h-10 px-3"
            data-testid="back-btn"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <div className="font-heading font-black text-xl text-slate-900">
              {fundraiser?.name || "Fundraiser"}
            </div>
            <div className="text-sm text-slate-500">{fundraiser?.email}</div>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* Insights */}
        {metrics?.insights && metrics.insights.length > 0 && (
          <div className="space-y-3" data-testid="insights-section">
            {metrics.insights.map((insight, index) => (
              <div 
                key={`insight-${insight.slice(0, 20)}-${index}`}
                className="flex items-start gap-3 p-4 rounded-xl bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-100"
              >
                <Lightbulb className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-slate-700">{insight}</p>
              </div>
            ))}
          </div>
        )}

        {/* OCI V2 — Gift Aid performance for this fundraiser */}
        <GiftAidMetricsStrip variant="fundraiser" fundraiserId={fundraiserId} />

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="stats-grid">
          {stats.map((stat, index) => (
            <Card key={`stat-${stat.label}`} className="border-slate-200 shadow-none">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <stat.icon className={`h-4 w-4 ${stat.color}`} />
                  <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                    {stat.label}
                  </span>
                </div>
                <div className="font-heading font-black text-2xl text-slate-900">
                  {stat.value}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Engagement Score Comparison */}
        <Card className="border-slate-200 shadow-none">
          <CardContent className="p-4">
            <h3 className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-4">
              Engagement Score (Retained vs All)
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-3 rounded-lg bg-emerald-50">
                <div className="font-heading font-black text-2xl text-emerald-600">
                  {metrics?.avgEngagementScoreRetained?.toFixed(1) || "0"}
                </div>
                <div className="text-xs text-slate-500 mt-1">Retained Avg</div>
              </div>
              <div className="text-center p-3 rounded-lg bg-slate-50">
                <div className="font-heading font-black text-2xl text-slate-900">
                  {metrics?.avgEngagementScoreAll?.toFixed(1) || "0"}
                </div>
                <div className="text-xs text-slate-500 mt-1">All Avg</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Retention Stats */}
        <Card className="border-slate-200 shadow-none">
          <CardContent className="p-4">
            <h3 className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-4">
              Retention Metrics
            </h3>
            <div className="grid grid-cols-3 gap-4">
              {retentionStats.map((stat, index) => (
                <div key={`retention-${stat.label}`} className="text-center">
                  <div className="font-heading font-black text-xl text-slate-900">
                    {stat.value}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Lottery Stats */}
        <Card className="border-slate-200 shadow-none">
          <CardContent className="p-4">
            <h3 className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-4">
              Lottery Performance
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-3 rounded-lg bg-slate-50">
                <div className="font-heading font-black text-2xl text-slate-900">
                  {metrics?.lotterySingleCount || 0}
                </div>
                <div className="text-xs text-slate-500 mt-1">Single Entries</div>
              </div>
              <div className="text-center p-3 rounded-lg bg-emerald-50">
                <div className="font-heading font-black text-2xl text-emerald-600">
                  {metrics?.lotteryDoubleCount || 0}
                </div>
                <div className="text-xs text-slate-500 mt-1">Double Entries</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
